import React from 'react';
import { LegalLayout } from '@/components/layout/LegalLayout';

export default function TermsOfService() {
  return (
    <LegalLayout title="Terms of Service">
      <p className="mb-8 text-slate-500 dark:text-slate-400"><strong>Last updated:</strong> June 11, 2026</p>

      <p className="mb-8">
        These Terms of Service ("Terms") govern your use of ResumePro ("we," "us," or "our"), an application built and operated by Farhad Rezaei. By accessing or using ResumePro, you agree to be bound by these Terms. If you disagree with any part of these Terms, you may not access or use the application.
      </p>

      <section>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">1. Local-First Architecture and Data Storage</h2>
        <p className="mb-4">
          ResumePro operates on a strict <strong className="text-slate-900 dark:text-white font-semibold">local-first privacy model</strong>. As detailed in our Privacy Policy, your resumes, job histories, and workspace drafts are never stored on our servers or databases.
        </p>
        <ul className="space-y-3 mb-8">
          <li className="flex gap-3 items-start">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span><strong className="text-slate-900 dark:text-white font-semibold">Device Storage:</strong> All of your application state - including drafts, work history, and user workspaces - is stored locally in your browser's storage on your physical device.</span>
          </li>
          <li className="flex gap-3 items-start">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span><strong className="text-slate-900 dark:text-white font-semibold">Responsibility for Data Loss:</strong> Because we do not store this data remotely and have no access to it, we cannot recover backups if you clear your browser caches, uninstall your browser, or lose access to your device. We strongly recommend regularly exporting your completed resumes.</span>
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">2. AI-Generated Content Disclaimer</h2>
        <p className="mb-4">
          ResumePro utilizes third-party Artificial Intelligence services (Google's Gemini API) to analyze inputs and generate suggested resume bullet points, cover letters, and other materials.
        </p>
        <div className="bg-amber-50 dark:bg-amber-500/10 border-l-4 border-amber-500 p-5 rounded-r-xl mb-4">
          <strong className="text-amber-800 dark:text-amber-400 font-semibold block mb-2">Important Disclaimer:</strong>
          <p className="text-amber-700 dark:text-amber-500 text-sm">
            AI algorithms can generate inaccurate information ("hallucinations"), inject errors, or omit vital context. By using ResumePro, you acknowledge that you are solely responsible for reviewing, verifying, editing, and ensuring the absolute accuracy, truthfulness, and legality of any document before saving or distributing it to employers or third parties.
          </p>
        </div>
        <p className="mb-8">ResumePro assumes no responsibility or liability for factual inaccuracies, missed employment opportunities, professional misrepresentation, or reputational damage resulting from your use of AI-generated content.</p>
      </section>

      <section>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">3. Acceptable Use and Restrictions</h2>
        <p className="mb-4">
          By using ResumePro, you strictly agree not to:
        </p>
        <ul className="space-y-3 mb-8">
          <li className="flex gap-3 items-start">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span>Submit malicious, illegal, or prohibited content into the AI processors.</span>
          </li>
          <li className="flex gap-3 items-start">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span>Attempt to bypass usage limits or use automated tools, bots, or scrapers to access the service.</span>
          </li>
          <li className="flex gap-3 items-start">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span>Reverse engineer or attempt to extract the underlying technology or system prompt instructions.</span>
          </li>
          <li className="flex gap-3 items-start">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span>Sell, rent, or lease access to your ResumePro account to third parties.</span>
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">4. Intellectual Property</h2>
        <p className="mb-8">
          The underlying design interfaces, branding, copy, architecture, and code of ResumePro remain our protected intellectual property. However, <strong className="text-slate-900 dark:text-white font-semibold">you retain 100% full copyright and ownership</strong> over the final text, resumes, and documents generated during your active usage of the tool, provided they are based on your personal unique inputs.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">5. Limitation of Liability</h2>
        <p className="mb-8">
          To the absolute maximum extent permitted by applicable law, in no event shall ResumePro, Farhad Rezaei, its developers, or its affiliates be liable for any indirect, incidental, special, consequential, or punitive damages - including but not limited to loss of data, loss of anticipated employment, or emotional distress - resulting from your reliance on or use of the application. The service is provided "as is" without warranty of any kind.
        </p>
      </section>
      
      <section>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">6. Changes to Terms</h2>
        <p className="mb-8">
          We reserve the right to modify or replace these Terms at any time at our sole discretion. Material changes will be reflected by updating the "Last updated" date at the top of these Terms. Your continued use of the platform after updates are published implicitly constitutes acceptance of the new Terms.
        </p>
      </section>
      
      <section>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">7. Contact</h2>
        <p>
          If you have any questions about these Terms, please contact us at <a href="mailto:farhadrezaie73@gmail.com" className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium">farhadrezaie73@gmail.com</a>.
        </p>
      </section>
    </LegalLayout>
  );
}
